import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Upload, X, ChevronDown } from 'lucide-react';
import axiosInstance from '../../../lib/axios';
import defaultColleges from '../data/colleges.json';

const YEARS = [
  { value: '1', label: '1st Year' },
  { value: '2', label: '2nd Year' },
  { value: '3', label: '3rd Year' },
  { value: '4', label: '4th Year' },
  { value: '5', label: '5th Year' },
  { value: 'intern', label: 'Intern' },
];

const COUNTRY_CODES = [
  { flag: '🇮🇳', name: 'India', code: '+91' },
  { flag: '🇺🇸', name: 'United States', code: '+1' },
  // { flag: '🇬🇧', name: 'United Kingdom', code: '+44'  },
  // { flag: '🇦🇺', name: 'Australia',      code: '+61'  },
  // { flag: '🇨🇦', name: 'Canada',         code: '+1'   },
  // { flag: '🇳🇿', name: 'New Zealand',    code: '+64'  },
  // { flag: '🇿🇦', name: 'South Africa',   code: '+27'  },
  // { flag: '🇳🇬', name: 'Nigeria',        code: '+234' },
  // { flag: '🇵🇰', name: 'Pakistan',       code: '+92'  },
  // { flag: '🇧🇩', name: 'Bangladesh',     code: '+880' },
  // { flag: '🇱🇰', name: 'Sri Lanka',      code: '+94'  },
  // { flag: '🇳🇵', name: 'Nepal',          code: '+977' },
  // { flag: '🇲🇾', name: 'Malaysia',       code: '+60'  },
  // { flag: '🇸🇬', name: 'Singapore',      code: '+65'  },
  // { flag: '🇦🇪', name: 'UAE',            code: '+971' },
  // { flag: '🇸🇦', name: 'Saudi Arabia',   code: '+966' },
  // { flag: '🇩🇪', name: 'Germany',        code: '+49'  },
  // { flag: '🇫🇷', name: 'France',         code: '+33'  },
  // { flag: '🇮🇹', name: 'Italy',          code: '+39'  },
  // { flag: '🇳🇱', name: 'Netherlands',    code: '+31'  },
  // { flag: '🇮🇪', name: 'Ireland',        code: '+353' },
  // { flag: '🇨🇭', name: 'Switzerland',    code: '+41'  },
  // { flag: '🇸🇪', name: 'Sweden',         code: '+46'  },
  // { flag: '🇳🇴', name: 'Norway',         code: '+47'  },
  // { flag: '🇩🇰', name: 'Denmark',        code: '+45'  },
  // { flag: '🇨🇳', name: 'China',          code: '+86'  },
  // { flag: '🇯🇵', name: 'Japan',          code: '+81'  },
  // { flag: '🇰🇷', name: 'South Korea',    code: '+82'  },
  // { flag: '🇵🇭', name: 'Philippines',    code: '+63'  },
  // { flag: '🇧🇷', name: 'Brazil',         code: '+55'  },
  // { flag: '🇲🇽', name: 'Mexico',         code: '+52'  },
  // { flag: '🇦🇷', name: 'Argentina',      code: '+54'  },
  // { flag: '🇰🇪', name: 'Kenya',          code: '+254' },
  // { flag: '🇬🇭', name: 'Ghana',          code: '+233' },
  // { flag: '🇿🇼', name: 'Zimbabwe',       code: '+263' },
];

const StudentInfoForm = ({ onNext, onBack, initialData }) => {
  const [colleges, setColleges] = useState(defaultColleges || []);
  const [collegesLoading, setCollegesLoading] = useState(false);
  const [idFile, setIdFile] = useState(initialData?.collegeIdFile || null);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [fileError, setFileError] = useState('');

  // Phone state
  const [countryCode, setCountryCode] = useState('+91');
  const [localPhone, setLocalPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      yearOfStudy: initialData?.yearOfStudy || '',
      collegeEmail: initialData?.collegeEmail || '',
      collegeId: initialData?.collegeId || '',
    },
  });

  const watchedCollegeId = watch('collegeId');

  // Fetch colleges (background refresh, keeps bundled list on any network error)
  useEffect(() => {
    let isMounted = true;
    const fetchColleges = async () => {
      try {
        const res = await axiosInstance.get('/colleges');
        const liveList = res.data?.data?.colleges || res.data?.data;
        if (isMounted && Array.isArray(liveList) && liveList.length > 0) {
          setColleges(liveList);
        }
      } catch (err) {
        console.warn('[StudentInfoForm] API fetch failed, using bundled recognized colleges:', err?.message);
      } finally {
        if (isMounted) {
          setCollegesLoading(false);
        }
      }
    };
    fetchColleges();
    return () => {
      isMounted = false;
    };
  }, []);

  // Sync selected college label
  useEffect(() => {
    if (watchedCollegeId) {
      const col = colleges.find((c) => c.id === watchedCollegeId);
      setSelectedCollege(col || null);
      if (col) setCollegeSearch(col.name);
    }
  }, [watchedCollegeId, colleges]);

  // Pre-fill phone from initialData
  useEffect(() => {
    if (initialData?.phone) {
      const matched = COUNTRY_CODES.find((c) => initialData.phone.startsWith(c.code));
      if (matched) {
        setCountryCode(matched.code);
        setLocalPhone(initialData.phone.slice(matched.code.length));
      } else {
        setLocalPhone(initialData.phone.replace(/^\+/, ''));
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredColleges = colleges.filter((c) =>
    c.name.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  const validatePhone = () => {
    if (!localPhone) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (!/^[0-9]{10}$/.test(localPhone)) {
      setPhoneError('Enter a valid 10-digit phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const onSubmit = (data) => {
    if (!validatePhone()) return;

    if (!idFile) {
      setFileError('Please upload your student ID');
      return;
    }

    const fullPhone = `${countryCode}${localPhone}`;

    onNext({
      ...data,
      phone: fullPhone,
      emailType: 'college',
      collegeIdFile: idFile,
      email: data.collegeEmail,
      collegeEmail: data.collegeEmail,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) {
        setFileError('File size must be under 1MB.');
        e.target.value = '';
        return;
      }
      setIdFile(file);
      setFileError('');
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mb-0.5">Your Information</h2>
      <p className="text-gray-500 mb-4 text-sm">Tell us a bit about yourself</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">

        {/* Name */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('firstName', { 
                required: 'First name is required',
                validate: {
                  noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed'
                }
              })}
              placeholder="Jane"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              {...register('lastName', {
                validate: {
                  noEmoji: (val) => !val || !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed'
                }
              })}
              placeholder="Smith"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Phone with Country Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>

          <div
            className={`flex rounded-lg overflow-hidden border bg-gray-50 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent transition-all ${phoneError ? 'border-red-400' : 'border-gray-200'
              }`}
          >
            {/* Country Code Selector */}
            <div className="relative flex-shrink-0">
              <select
                id="countryCode"
                value={countryCode}
                onChange={(e) => setCountryCode(e?.target?.value ?? e ?? '+91')}
                className="h-full pl-3 pr-7 py-2 bg-gray-100 border-r border-gray-200 text-sm font-medium text-gray-700 focus:outline-none appearance-none cursor-pointer"
                style={{ minWidth: '94px' }}
              >
                {COUNTRY_CODES.map((c, i) => (
                  <option key={`${c.code}-${i}`} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <ChevronDown size={12} className="text-gray-400" />
              </div>
            </div>

            {/* Local Number */}
            <input
              id="localPhone"
              type="tel"
              value={localPhone}
              onChange={(e) => {
                const raw = e?.target?.value ?? e ?? '';
                let val = String(raw).replace(/\D/g, '');
                if (val.startsWith('0')) {
                  val = val.replace(/^0+/, '');
                }
                if (val.length > 10) {
                  val = val.substring(0, 10);
                }
                setLocalPhone(val);
                if (phoneError) setPhoneError('');
              }}
              placeholder="9999999999"
              maxLength={11}
              className="flex-1 px-3 py-2 bg-transparent text-sm focus:outline-none min-w-0"
            />
          </div>

          {phoneError && (
            <p className="mt-1 text-xs text-red-500">{phoneError}</p>
          )}

          {localPhone && !phoneError && (
            <p className="mt-1 text-xs text-gray-400">
            </p>
          )}
        </div>

        {/* Student Verification Header */}
        <div className="pt-1">
          <h3 className="text-sm font-semibold text-gray-900 border-t border-gray-100 pt-2 mb-2">
            Student Verification
          </h3>
        </div>

        {/* College Search */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            University/College <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={collegeSearch}
              onChange={(e) => {
                const val = e?.target?.value ?? e ?? '';
                setCollegeSearch(val);
                setShowDropdown(true);
                if (!val) {
                  setValue('collegeId', '');
                  setSelectedCollege(null);
                }
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder={collegesLoading ? 'Loading colleges...' : 'Select your institution'}
              disabled={collegesLoading}
              className="w-full pl-3 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all cursor-pointer"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
          <input
            type="hidden"
            {...register('collegeId', { required: 'Please select your college' })}
          />

          {showDropdown && filteredColleges.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {filteredColleges.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={() => {
                    setValue('collegeId', c.id, { shouldValidate: true });
                    setSelectedCollege(c);
                    setCollegeSearch(c.name);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-medium">{c.name}</div>
                  {c.domain && <div className="text-xs text-gray-400">@{c.domain}</div>}
                </button>
              ))}
            </div>
          )}
          {errors.collegeId && (
            <p className="mt-1 text-xs text-red-500">{errors.collegeId.message}</p>
          )}
        </div>

        {/* Year of Study */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Year of Study <span className="text-red-500">*</span>
          </label>
          <select
            {...register('yearOfStudy', { required: 'Please select your year' })}
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          >
            <option value="">Select your year</option>
            {YEARS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.yearOfStudy && (
            <p className="mt-1 text-xs text-red-500">{errors.yearOfStudy.message}</p>
          )}
        </div>

        {/* College Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            College Email <span className="text-red-500">*</span>
          </label>
          <input
            {...register('collegeEmail', {
              required: 'College email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
              validate: {
                noEmoji: (val) => !/\p{Extended_Pictographic}/u.test(val) || 'Emojis are not allowed in email'
              }
            })}
            type="email"
            placeholder="yourname@university.edu"
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
          <p className="mt-1 text-xs text-gray-400">This will be your username</p>
          {errors.collegeEmail && (
            <p className="mt-1 text-xs text-red-500">{errors.collegeEmail.message}</p>
          )}
          {selectedCollege?.domain && (
            <p className="mt-1 text-xs text-indigo-600 font-medium">
              Must end with @{selectedCollege.domain}
            </p>
          )}
        </div>

        {/* College ID Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload College ID <span className="text-red-500">*</span>
          </label>
          {idFile ? (
            <div className="flex items-center gap-3 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex-1 text-xs text-indigo-700 truncate">{idFile.name}</div>
              <button
                type="button"
                onClick={() => setIdFile(null)}
                className="text-indigo-400 hover:text-indigo-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center gap-1.5 p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100/50 hover:border-indigo-300 transition-all text-center">
              <Upload size={18} className="text-gray-400 mb-0.5" />
              <span className="text-xs font-semibold text-gray-700">Click to upload student ID</span>
              <span className="text-[10px] text-gray-400">PNG, JPG, or PDF up to 1MB</span>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={handleFileChange}
              />
            </label>
          )}
          {fileError && <p className="mt-1 text-xs text-red-500">{fileError}</p>}
        </div>

        <button
          type="submit"
          className="mt-3 w-full flex items-center justify-center py-2.5 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-sm"
        >
          Continue
        </button>

      </form>
    </div>
  );
};

export default StudentInfoForm;
